import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FiArrowLeft,
  FiUser,
  FiBriefcase,
  FiHome,
  FiCreditCard,
  FiTrendingUp,
  FiShoppingCart,
  FiCheckSquare,
  FiEdit2,
  FiSave,
  FiDownload,
  FiFileText,
  FiFile,
  FiInfo,
  FiShield,
  FiLink,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { exportToPDF, exportToCSV, exportToExcel } from "@/lib/factFinderExport";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CardSkeleton } from "@/components/common";
import { factFinderConfig, getActiveSections } from "@/config/fact-finder";
import type {
  ApplicantData,
  EmploymentData,
  AssetsData,
  LiabilitiesData,
  IncomeData,
  ExpensesData,
  RequirementsData,
  FactFinderSection,
} from "@/types/fact-finder";

interface DecryptedFactFinder {
  id: string;
  customerId: string | null;
  accessToken: string | null;
  status: "draft" | "submitted" | "in_review" | "approved" | "archived";
  completedSections: string[];
  lastSectionSaved: string | null;
  applicant1: ApplicantData | null;
  applicant2: ApplicantData | null;
  employment: EmploymentData | null;
  assets: AssetsData | null;
  liabilities: LiabilitiesData | null;
  income: IncomeData | null;
  expenses: ExpensesData | null;
  requirements: RequirementsData | null;
  adminNotes: string | null;
  privacyConsentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const sectionIcons: Record<string, typeof FiUser> = {
  applicants: FiUser,
  applicant1: FiUser,
  applicant2: FiUser,
  employment: FiBriefcase,
  assets: FiHome,
  liabilities: FiCreditCard,
  income: FiTrendingUp,
  expenses: FiShoppingCart,
  requirements: FiCheckSquare,
  "next-steps": FiInfo,
  consent: FiShield,
};

const sectionLabels: Record<string, string> = {
  applicants: "Personal Details",
  applicant1: "Applicant 1",
  applicant2: "Applicant 2",
  employment: "Employment",
  assets: "Assets",
  liabilities: "Liabilities",
  income: "Income",
  expenses: "Expenses",
  requirements: "Requirements",
  "next-steps": "Next Steps",
  consent: "Privacy & Consent",
};

export function FactFinderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [factFinder, setFactFinder] = useState<DecryptedFactFinder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const fetchFactFinder = useCallback(async () => {
    if (!id) return;

    try {
      const response = await fetch(`/api/admin/fact-finders/${id}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as DecryptedFactFinder;
        setFactFinder(data);
        setAdminNotes(data.adminNotes || "");
      } else if (response.status === 404) {
        toast.error("Fact finder not found");
        navigate("/admin/fact-finders");
      }
    } catch (error) {
      console.error("Failed to fetch fact finder:", error);
      toast.error("Failed to load fact finder");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchFactFinder();
  }, [fetchFactFinder]);

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;

    try {
      const response = await fetch(`/api/admin/fact-finders/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success("Status updated");
        setFactFinder((prev) =>
          prev ? { ...prev, status: newStatus as DecryptedFactFinder["status"] } : null
        );
      } else {
        toast.error("Failed to update status");
      }
    } catch {
      toast.error("An error occurred");
    }
  };

  const handleSaveNotes = async () => {
    if (!id) return;

    setIsSavingNotes(true);
    try {
      const response = await fetch(`/api/admin/fact-finders/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminNotes }),
      });

      if (response.ok) {
        toast.success("Notes saved");
      } else {
        toast.error("Failed to save notes");
      }
    } catch {
      toast.error("An error occurred");
    } finally {
      setIsSavingNotes(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/fact-finders">
              <FiArrowLeft className="size-4 mr-2" />
              Back
            </Link>
          </Button>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <CardSkeleton />
            <CardSkeleton />
          </div>
          <CardSkeleton />
        </div>
      </div>
    );
  }

  if (!factFinder) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/fact-finders">
              <FiArrowLeft className="size-4 mr-2" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Fact Finder Details</h1>
            <p className="text-muted-foreground text-sm">
              ID: {factFinder.id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {factFinder.accessToken && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                const publicUrl = `${window.location.origin}/fact-finder/${factFinder.accessToken}`;
                navigator.clipboard.writeText(publicUrl);
                toast.success("Public link copied to clipboard");
              }}
            >
              <FiLink className="size-4" />
              Copy Link
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <FiDownload className="size-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  exportToPDF(factFinder);
                  toast.success("PDF exported successfully");
                }}
                className="gap-2 cursor-pointer"
              >
                <FiFileText className="size-4" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  exportToCSV(factFinder);
                  toast.success("CSV exported successfully");
                }}
                className="gap-2 cursor-pointer"
              >
                <FiFile className="size-4" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  exportToExcel(factFinder);
                  toast.success("Excel exported successfully");
                }}
                className="gap-2 cursor-pointer"
              >
                <FiFile className="size-4" />
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Select value={factFinder.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="in_review">In Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Applicant 1 */}
          {factFinder.applicant1 && (
            <SectionCard
              title="Applicant 1"
              icon={FiUser}
              isCompleted={factFinder.completedSections.includes("applicant1")}
            >
              <ApplicantDetails data={factFinder.applicant1} />
            </SectionCard>
          )}

          {/* Applicant 2 */}
          {factFinder.applicant2 && (
            <SectionCard
              title="Applicant 2"
              icon={FiUser}
              isCompleted={factFinder.completedSections.includes("applicant2")}
            >
              <ApplicantDetails data={factFinder.applicant2} />
            </SectionCard>
          )}

          {/* Employment */}
          {factFinder.employment && (
            <SectionCard
              title="Employment"
              icon={FiBriefcase}
              isCompleted={factFinder.completedSections.includes("employment")}
            >
              <EmploymentDetails data={factFinder.employment} />
            </SectionCard>
          )}

          {/* Assets */}
          {factFinder.assets && (
            <SectionCard
              title="Assets"
              icon={FiHome}
              isCompleted={factFinder.completedSections.includes("assets")}
            >
              <AssetsDetails data={factFinder.assets} />
            </SectionCard>
          )}

          {/* Liabilities */}
          {factFinder.liabilities && (
            <SectionCard
              title="Liabilities"
              icon={FiCreditCard}
              isCompleted={factFinder.completedSections.includes("liabilities")}
            >
              <LiabilitiesDetails data={factFinder.liabilities} />
            </SectionCard>
          )}

          {/* Income */}
          {factFinder.income && (
            <SectionCard
              title="Income"
              icon={FiTrendingUp}
              isCompleted={factFinder.completedSections.includes("income")}
            >
              <IncomeDetails data={factFinder.income} />
            </SectionCard>
          )}

          {/* Expenses */}
          {factFinder.expenses && (
            <SectionCard
              title="Expenses"
              icon={FiShoppingCart}
              isCompleted={factFinder.completedSections.includes("expenses")}
            >
              <ExpensesDetails data={factFinder.expenses} />
            </SectionCard>
          )}

          {/* Requirements */}
          {factFinder.requirements && (
            <SectionCard
              title="Requirements & Objectives"
              icon={FiCheckSquare}
              isCompleted={factFinder.completedSections.includes("requirements")}
            >
              <RequirementsDetails data={factFinder.requirements} />
            </SectionCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Completion Progress</CardTitle>
              {!factFinderConfig.advancedMode && (
                <p className="text-xs text-muted-foreground">Basic mode enabled</p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {(getActiveSections(factFinderConfig) as readonly FactFinderSection[]).map((section) => {
                const Icon = sectionIcons[section] || FiCheckSquare;
                const label = sectionLabels[section] || section;
                const isCompleted = factFinder.completedSections.includes(section);
                return (
                  <div key={section} className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCompleted ? "bg-green-100 text-green-600" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      <Icon className="size-4" />
                    </div>
                    <span className={isCompleted ? "text-foreground" : "text-muted-foreground"}>
                      {label}
                    </span>
                    {isCompleted && (
                      <Badge variant="outline" className="ml-auto text-green-600">
                        Done
                      </Badge>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Privacy Consent */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Privacy Consent</CardTitle>
            </CardHeader>
            <CardContent>
              {factFinder.privacyConsentAt ? (
                <div className="text-sm">
                  <Badge variant="outline" className="text-green-600 mb-2">
                    Consented
                  </Badge>
                  <p className="text-muted-foreground">
                    {new Date(factFinder.privacyConsentAt).toLocaleString()}
                  </p>
                </div>
              ) : (
                <Badge variant="outline" className="text-yellow-600">
                  Not yet consented
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* Admin Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FiEdit2 className="size-4" />
                Admin Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={5}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Add private notes about this application..."
              />
              <Button
                size="sm"
                onClick={handleSaveNotes}
                disabled={isSavingNotes}
                className="w-full"
              >
                <FiSave className="size-4 mr-2" />
                {isSavingNotes ? "Saving..." : "Save Notes"}
              </Button>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{new Date(factFinder.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Updated</span>
                <span>{new Date(factFinder.updatedAt).toLocaleDateString()}</span>
              </div>
              {factFinder.lastSectionSaved && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Section</span>
                  <span>{sectionLabels[factFinder.lastSectionSaved]}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Helper Components

function SectionCard({
  title,
  icon: Icon,
  isCompleted,
  children,
}: {
  title: string;
  icon: typeof FiUser;
  isCompleted: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className="size-4" />
            {title}
          </CardTitle>
          {isCompleted && (
            <Badge variant="outline" className="text-green-600">
              Completed
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DataRow({ label, value }: { label: string; value: string | number | undefined | null }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between py-1 border-b border-border/50 last:border-0">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function ApplicantDetails({ data }: { data: ApplicantData }) {
  const { personal, currentAddress } = data;

  const formatContactMethod = (method: string | undefined) => {
    if (!method) return undefined;
    return method.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const fullName = `${personal.title || ""} ${personal.firstName} ${personal.middleNames || ""} ${personal.surname}`.trim().replace(/\s+/g, " ");

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium mb-2">Personal Details</h4>
        <div className="bg-muted/50 rounded-lg p-3 space-y-1">
          <DataRow label="Title" value={personal.title} />
          <DataRow label="First Name" value={personal.firstName} />
          <DataRow label="Middle Name" value={personal.middleNames} />
          <DataRow label="Last Name" value={personal.surname} />
          <DataRow label="Full Name" value={fullName} />
          <DataRow label="Gender" value={personal.gender} />
          <DataRow label="Date of Birth" value={personal.dateOfBirth} />
          {personal.maritalStatus && <DataRow label="Marital Status" value={personal.maritalStatus} />}
          {personal.numberOfDependants !== undefined && personal.numberOfDependants > 0 && (
            <DataRow label="Dependants" value={personal.numberOfDependants} />
          )}
          {personal.isSelfEmployed !== undefined && (
            <DataRow label="Self Employed" value={personal.isSelfEmployed ? "Yes" : "No"} />
          )}
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-2">Contact Details</h4>
        <div className="bg-muted/50 rounded-lg p-3 space-y-1">
          {personal.preferredContactMethod && (
            <DataRow label="Preferred Contact" value={formatContactMethod(personal.preferredContactMethod)} />
          )}
          <DataRow label="Email" value={personal.email} />
          <DataRow label="Mobile" value={personal.mobile} />
          <DataRow label="Home Phone" value={personal.homePhone} />
          <DataRow label="Work Phone" value={personal.workPhone} />
          <DataRow label="Country of Residence" value={personal.countryOfResidence} />
        </div>
      </div>

      {/* Only show address section if there's data (advanced mode) */}
      {(currentAddress.address || currentAddress.suburb) && (
        <div>
          <h4 className="font-medium mb-2">Current Address</h4>
          <div className="bg-muted/50 rounded-lg p-3 space-y-1">
            <DataRow label="Address" value={currentAddress.address} />
            {currentAddress.suburb && (
              <DataRow label="Suburb" value={`${currentAddress.suburb}, ${currentAddress.state} ${currentAddress.postcode}`} />
            )}
            <DataRow label="Residency Type" value={currentAddress.residencyType?.replace("_", " ")} />
            {currentAddress.rentPerWeek && (
              <DataRow label="Rent per Week" value={`$${currentAddress.rentPerWeek}`} />
            )}
            <DataRow label="Moved In" value={currentAddress.dateMovedIn} />
          </div>
        </div>
      )}
    </div>
  );
}

function EmploymentDetails({ data }: { data: EmploymentData }) {
  const renderEmployment = (emp: typeof data.applicant1, label: string) => (
    <div className="space-y-2">
      <h4 className="font-medium">{label}</h4>
      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
        <DataRow label="Type" value={emp.current.employmentType.replace("_", " ")} />
        <DataRow label="Employer" value={emp.current.employerName} />
        <DataRow label="Occupation" value={emp.current.occupation} />
        <DataRow label="Start Date" value={emp.current.startDate} />
        <DataRow label="Sector" value={emp.current.employerType.replace("_", " ")} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {renderEmployment(data.applicant1, "Applicant 1 Employment")}
      {data.applicant2 && renderEmployment(data.applicant2, "Applicant 2 Employment")}
    </div>
  );
}

function AssetsDetails({ data }: { data: AssetsData }) {
  const totalAssets =
    data.properties.reduce((sum, p) => sum + p.currentValue, 0) +
    data.savings.reduce((sum, s) => sum + s.amount, 0) +
    data.shares.reduce((sum, s) => sum + s.value, 0) +
    data.superannuation.reduce((sum, s) => sum + s.value, 0) +
    data.vehicles.reduce((sum, v) => sum + v.value, 0);

  return (
    <div className="space-y-4">
      <div className="bg-accent/20 rounded-lg p-3">
        <p className="text-sm text-muted-foreground">Total Assets</p>
        <p className="text-2xl font-bold">${totalAssets.toLocaleString()}</p>
      </div>

      {data.properties.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Properties ({data.properties.length})</h4>
          {data.properties.map((p, i) => (
            <div key={i} className="bg-muted/50 rounded-lg p-3 mb-2 space-y-1">
              <DataRow label="Address" value={p.address} />
              <DataRow label="Value" value={`$${p.currentValue.toLocaleString()}`} />
              <DataRow label="Type" value={p.propertyType} />
              {p.loanBalance && <DataRow label="Loan Balance" value={`$${p.loanBalance.toLocaleString()}`} />}
            </div>
          ))}
        </div>
      )}

      {data.savings.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Savings ({data.savings.length})</h4>
          {data.savings.map((s, i) => (
            <div key={i} className="bg-muted/50 rounded-lg p-3 mb-2 space-y-1">
              <DataRow label="Institution" value={s.bankInstitution} />
              <DataRow label="Amount" value={`$${s.amount.toLocaleString()}`} />
            </div>
          ))}
        </div>
      )}

      {data.vehicles.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Vehicles ({data.vehicles.length})</h4>
          {data.vehicles.map((v, i) => (
            <div key={i} className="bg-muted/50 rounded-lg p-3 mb-2 space-y-1">
              <DataRow label="Vehicle" value={`${v.year} ${v.make} ${v.model}`} />
              <DataRow label="Value" value={`$${v.value.toLocaleString()}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiabilitiesDetails({ data }: { data: LiabilitiesData }) {
  const totalLoans = data.loans.reduce((sum, l) => sum + l.amountOwing, 0);
  const totalCards = data.creditCards.reduce((sum, c) => sum + c.amountOwing, 0);

  return (
    <div className="space-y-4">
      <div className="bg-destructive/10 rounded-lg p-3">
        <p className="text-sm text-muted-foreground">Total Liabilities</p>
        <p className="text-2xl font-bold">${(totalLoans + totalCards).toLocaleString()}</p>
      </div>

      {data.loans.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Loans ({data.loans.length})</h4>
          {data.loans.map((l, i) => (
            <div key={i} className="bg-muted/50 rounded-lg p-3 mb-2 space-y-1">
              <DataRow label="Type" value={l.loanType} />
              <DataRow label="Lender" value={l.lender} />
              <DataRow label="Owing" value={`$${l.amountOwing.toLocaleString()}`} />
              <DataRow label="Repayment" value={`$${l.repaymentAmount} ${l.repaymentFrequency}`} />
            </div>
          ))}
        </div>
      )}

      {data.creditCards.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Credit Cards ({data.creditCards.length})</h4>
          {data.creditCards.map((c, i) => (
            <div key={i} className="bg-muted/50 rounded-lg p-3 mb-2 space-y-1">
              <DataRow label="Card" value={c.cardType} />
              <DataRow label="Lender" value={c.lender} />
              <DataRow label="Limit" value={`$${c.limit.toLocaleString()}`} />
              <DataRow label="Owing" value={`$${c.amountOwing.toLocaleString()}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IncomeDetails({ data }: { data: IncomeData }) {
  const totalIncome =
    (data.applicant1Salary || 0) +
    (data.applicant2Salary || 0) +
    (data.businessProfit || 0) +
    (data.investmentIncome || 0) +
    (data.permanentPensions || 0) +
    (data.otherIncome || 0);

  return (
    <div className="space-y-4">
      <div className="bg-green-50 rounded-lg p-3">
        <p className="text-sm text-muted-foreground">Total Annual Income</p>
        <p className="text-2xl font-bold text-green-600">${totalIncome.toLocaleString()}</p>
      </div>
      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
        {data.applicant1Salary && <DataRow label="Applicant 1 Salary" value={`$${data.applicant1Salary.toLocaleString()}`} />}
        {data.applicant2Salary && <DataRow label="Applicant 2 Salary" value={`$${data.applicant2Salary.toLocaleString()}`} />}
        {data.businessProfit && <DataRow label="Business Profit" value={`$${data.businessProfit.toLocaleString()}`} />}
        {data.investmentIncome && <DataRow label="Investment Income" value={`$${data.investmentIncome.toLocaleString()}`} />}
        {data.permanentPensions && <DataRow label="Pensions" value={`$${data.permanentPensions.toLocaleString()}`} />}
        {data.otherIncome && <DataRow label="Other Income" value={`$${data.otherIncome.toLocaleString()}`} />}
      </div>
    </div>
  );
}

function ExpensesDetails({ data }: { data: ExpensesData }) {
  return (
    <div className="space-y-4">
      <div className="bg-orange-50 rounded-lg p-3">
        <p className="text-sm text-muted-foreground">Total Monthly Expenses</p>
        <p className="text-2xl font-bold text-orange-600">${data.totalMonthly.toLocaleString()}</p>
      </div>
      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
        <DataRow label="Groceries" value={`$${data.groceries}`} />
        <DataRow label="Personal" value={`$${data.personal}`} />
        <DataRow label="Property (Owner Occupied)" value={`$${data.propertyOwnerOccupied}`} />
        <DataRow label="Property (Investment)" value={`$${data.propertyInvestment}`} />
        <DataRow label="Education" value={`$${data.education}`} />
        <DataRow label="Child Care" value={`$${data.childCare}`} />
        <DataRow label="Health" value={`$${data.health}`} />
        <DataRow label="Transport" value={`$${data.transport}`} />
        <DataRow label="Insurance" value={`$${data.insurance}`} />
        <DataRow label="Entertainment" value={`$${data.entertainment}`} />
        <DataRow label="Communication" value={`$${data.communication}`} />
        <DataRow label="Child Support" value={`$${data.childSupport}`} />
        {data.other > 0 && <DataRow label="Other" value={`$${data.other}`} />}
      </div>
    </div>
  );
}

function RequirementsDetails({ data }: { data: RequirementsData }) {
  const features = Object.entries(data.desiredFeatures)
    .filter(([, v]) => v)
    .map(([k]) => k.replace(/([A-Z])/g, " $1").trim());

  return (
    <div className="space-y-4">
      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
        <DataRow label="Primary Reason" value={data.primaryReason} />
        <DataRow label="Contract Length" value={data.expectedContractLength.replace("_", " ")} />
        <DataRow label="Interest Only" value={data.seekingInterestOnly ? "Yes" : "No"} />
        <DataRow label="Rate Concern" value={data.concernAboutRisingRates.replace("_", " ")} />
        <DataRow label="Has Defaults" value={data.hasDefaultsOrJudgements ? "Yes" : "No"} />
        <DataRow label="Commitments Up to Date" value={data.allCommitmentsUpToDate ? "Yes" : "No"} />
        <DataRow label="Expects Changes" value={data.expectsFinancialChanges ? "Yes" : "No"} />
        <DataRow label="Wants Adviser Intro" value={data.wantsFinancialAdviserIntro ? "Yes" : "No"} />
      </div>
      {features.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Desired Features</h4>
          <div className="flex flex-wrap gap-2">
            {features.map((f) => (
              <Badge key={f} variant="outline">
                {f}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
